import PropTypes from 'prop-types'
import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Checkbox, OutlinedInput } from '@mui/material'

import { useTranslation } from 'react-i18next'
import theme from '../../../../../theme'
import { HelperText, LabelContainer, RequiredIndicator, Textarea } from '../../../../generic/form'
import {
  CustomMenuItem,
  CustomMuiSelect,
} from '../../../../mermaidInputs/InputMuiChipSelectWithLabelAndValidation/InputMuiChipSelectWithLabelAndValidation.styles'
import Modal, { RightFooter } from '../../../../generic/Modal'
import {
  StyledModalInputRow,
  StyledModalFooterWrapper,
  StyledModalLeftFooter,
} from '../subPages/subPages.styles'
import { getFinanceSolutionInitialValues } from './financeSolutionInitialValues'
import { useFormik } from 'formik'
import { buttonGroupStates } from '../../../../../library/buttonGroupStates'
import { choicesPropType } from '../../../../../App/mermaidData/mermaidDataProptypes'
import { ButtonCaution, ButtonSecondary, IconButton } from '../../../../generic/buttons'
import SaveButton from './SaveButton'
import { getChips } from './modalHelpers'
import { useDatabaseSwitchboardInstance } from '../../../../../App/mermaidData/databaseSwitchboard/DatabaseSwitchboardContext'
import { useParams } from 'react-router'
import { toast } from 'react-toastify'
import { getToastArguments } from '../../../../../library/getToastArguments'
import { useHttpResponseErrorHandler } from '../../../../../App/HttpResponseErrorHandlerContext'
import InputNoRowWithLabelAndValidation from '../../../../mermaidInputs/InputNoRowWithLabelAndValidation'
import InputNoRowSelectWithLabelAndValidation from '../../../../mermaidInputs/InputNoRowSelectWithLabelAndValidation'
import GfcrIntegerInputField from '../GfcrIntegerInputField'
import { GFCR_MAX_INTEGER } from '../../../../../library/numbers/gfcrFieldMaximums'
import { getOptions } from '../../../../../library/getOptions'
import { IconInfo } from '../../../../icons'
import { displayErrorMessagesGFCR } from '../../../../../library/displayErrorMessagesGFCR'
import GfcrHelperLinks from '../subPages/GfcrHelperLinks'
import {
  BUSINESS_OR_FINANCIAL_MECHANISM_TYPES,
  LOCAL_ENTERPRISE_TYPES,
  NUMBER_OF_SOLUTIONS_SUPPORTED_BY_TYPES,
  isGenderSmartApplicable,
  isLocalEnterpriseApplicable,
  isUsedAnIncubatorApplicable,
} from './financeSolutionFieldVisibility'
import {
  getFinanceSolutionDuplicateKey,
  getFinanceSolutionDuplicateKeys,
} from './financeSolutionDuplicates'

const isTafNameVisible = (fs_type, used_an_incubator) =>
  BUSINESS_OR_FINANCIAL_MECHANISM_TYPES.includes(fs_type) &&
  !!used_an_incubator &&
  used_an_incubator !== 'none'

// A boolean false is a real answer for the yes/no fields, and 'none' is a real answer for
// used_an_incubator, so an unanswered field can only be recognised by a blank value rather
// than by falsiness.
const isBlank = (value) => value === '' || value === null || value === undefined

const FinanceSolutionModal = ({
  isOpen,
  onDismiss,
  financeSolution = undefined,
  indicatorSet,
  setIndicatorSet,
  choices,
  displayHelp,
}) => {
  const { t } = useTranslation()

  const indicatorSetSaveSuccessText = t('gfcr.success.indicator_set_save')
  const indicatorSetSaveFailedText = t('gfcr.errors.indicator_set_save_failed')

  const { databaseSwitchboardInstance } = useDatabaseSwitchboardInstance()
  const { projectId } = useParams()
  const handleHttpResponseError = useHttpResponseErrorHandler()

  const [isFormDirty, setIsFormDirty] = useState(false)
  const [saveButtonState, setSaveButtonState] = useState(buttonGroupStates.saved)
  const [isDeleting, setIsDeleting] = useState(false)

  const initialFormValues = useMemo(() => {
    return getFinanceSolutionInitialValues(financeSolution)
  }, [financeSolution])

  const handleFormSubmit = useCallback(
    async (formikValues, formikActions) => {
      setSaveButtonState(buttonGroupStates.saving)

      const isBusinessOrFinancialMechanism = BUSINESS_OR_FINANCIAL_MECHANISM_TYPES.includes(
        formikValues.fs_type,
      )
      const usedAnIncubator =
        formikValues.used_an_incubator === 'none' ? null : formikValues.used_an_incubator

      const formattedValues = {
        ...formikValues,
        id: financeSolution?.id,
        sector: formikValues.fs_type === 'business' ? formikValues.sector : '',
        geographical_coverage:
          formikValues.fs_type === 'ctf' ? formikValues.geographical_coverage : '',
        used_an_incubator: isBusinessOrFinancialMechanism ? usedAnIncubator : null,
        taf_name: isTafNameVisible(formikValues.fs_type, usedAnIncubator)
          ? formikValues.taf_name
          : '',
        local_enterprise: LOCAL_ENTERPRISE_TYPES.includes(formikValues.fs_type)
          ? formikValues.local_enterprise
          : false,
        gender_smart: isBusinessOrFinancialMechanism ? formikValues.gender_smart : false,
        number_of_solutions_supported_by: NUMBER_OF_SOLUTIONS_SUPPORTED_BY_TYPES.includes(
          formikValues.fs_type,
        )
          ? Number(formikValues.number_of_solutions_supported_by) || 0
          : 0,
        sustainable_finance_mechanisms:
          formikValues.fs_type === 'financial_mechanism'
            ? formikValues.sustainable_finance_mechanisms
            : [],
      }

      const existingFinanceSolutions = indicatorSet.finance_solutions

      let newFinanceSolutions

      if (formattedValues.id) {
        // Replace existing element
        newFinanceSolutions = existingFinanceSolutions.map((fs) =>
          fs.id === formattedValues.id ? formattedValues : fs,
        )
      } else {
        // Add a new element
        newFinanceSolutions = [...existingFinanceSolutions, formattedValues]
      }

      const updatedIndicatorSet = {
        ...indicatorSet,
        finance_solutions: newFinanceSolutions,
      }

      try {
        const response = await databaseSwitchboardInstance.saveIndicatorSet(
          projectId,
          updatedIndicatorSet,
        )

        setIndicatorSet(response)

        toast.success(...getToastArguments(indicatorSetSaveSuccessText))
      } catch (error) {
        setSaveButtonState(buttonGroupStates.unsaved)

        if (error) {
          displayErrorMessagesGFCR(error)

          handleHttpResponseError({
            error,
          })
        }
      }

      setSaveButtonState(buttonGroupStates.saved)
      onDismiss(formikActions.resetForm)
    },
    [
      databaseSwitchboardInstance,
      financeSolution,
      handleHttpResponseError,
      indicatorSet,
      onDismiss,
      projectId,
      setIndicatorSet,
      indicatorSetSaveSuccessText,
    ],
  )

  const formik = useFormik({
    initialValues: initialFormValues,
    enableReinitialize: true,
    onSubmit: handleFormSubmit,
    validate: (values) => {
      const errors = {}

      if (!values.name) {
        errors.name = [{ code: t('forms.required_field'), id: 'Required' }]
      }

      if (!values.fs_type) {
        errors.fs_type = [{ code: t('forms.required_field'), id: 'Required' }]
      }

      if (values.fs_type === 'programmatic_co_financing' && financeSolution?.revenues?.length > 0) {
        errors.fs_type = [
          {
            code: t('gfcr.forms.finance_solutions.pcf_revenues_error'),
            id: 'PCFRevenues',
          },
        ]
      }

      if (values.fs_type === 'business' && !values.sector) {
        errors.sector = [{ code: t('forms.required_field'), id: 'Required' }]
      }

      if (values.fs_type === 'ctf' && !values.geographical_coverage) {
        errors.geographical_coverage = [{ code: t('forms.required_field'), id: 'Required' }]
      }

      if (isUsedAnIncubatorApplicable(values.fs_type) && isBlank(values.used_an_incubator)) {
        errors.used_an_incubator = [{ code: t('forms.required_field'), id: 'Required' }]
      }

      if (isLocalEnterpriseApplicable(values.fs_type) && isBlank(values.local_enterprise)) {
        errors.local_enterprise = [{ code: t('forms.required_field'), id: 'Required' }]
      }

      if (isGenderSmartApplicable(values.fs_type) && isBlank(values.gender_smart)) {
        errors.gender_smart = [{ code: t('forms.required_field'), id: 'Required' }]
      }

      if (
        values.fs_type === 'financial_mechanism' &&
        !values.sustainable_finance_mechanisms?.length
      ) {
        errors.sustainable_finance_mechanisms = [
          { code: t('forms.required_field'), id: 'Required' },
        ]
      }

      // An indicator set can't hold two facilities / solutions with the same name and type. The
      // row being edited is excluded so it isn't flagged against itself. Only checked once name
      // and type are both filled in, so it never overwrites their required errors.
      const otherFinanceSolutions = indicatorSet.finance_solutions.filter(
        (existingFinanceSolution) => existingFinanceSolution.id !== financeSolution?.id,
      )
      const isDuplicate =
        !!values.name &&
        !!values.fs_type &&
        getFinanceSolutionDuplicateKeys(otherFinanceSolutions).has(
          getFinanceSolutionDuplicateKey(values),
        )

      if (isDuplicate) {
        errors.name = [{ code: t('gfcr.forms.finance_solutions.duplicate_error'), id: 'Duplicate' }]
      }

      return errors
    },
  })

  const handleDelete = useCallback(async () => {
    setIsDeleting(true)
    const updatedIndicatorSet = {
      ...indicatorSet,
      finance_solutions: indicatorSet.finance_solutions.filter(
        (fs) => fs.id !== financeSolution.id,
      ),
    }

    try {
      const response = await databaseSwitchboardInstance.saveIndicatorSet(
        projectId,
        updatedIndicatorSet,
      )

      setIndicatorSet(response)

      toast.success(...getToastArguments(indicatorSetSaveSuccessText))
    } catch (error) {
      if (error) {
        toast.error(...getToastArguments(indicatorSetSaveFailedText))

        handleHttpResponseError({
          error,
        })
      }
    }

    setIsDeleting(false)
    onDismiss(formik.resetForm)
  }, [
    databaseSwitchboardInstance,
    financeSolution,
    formik.resetForm,
    handleHttpResponseError,
    indicatorSet,
    onDismiss,
    projectId,
    setIndicatorSet,
    indicatorSetSaveSuccessText,
    indicatorSetSaveFailedText,
  ])

  const _setSaveButtonUnsaved = useEffect(() => {
    if (isFormDirty) {
      setSaveButtonState(buttonGroupStates.unsaved)
    }
  }, [isFormDirty])

  const _setIsFormDirty = useEffect(() => setIsFormDirty(!!formik.dirty), [formik.dirty])

  const fsTypeOptions = useMemo(
    () => getOptions(choices.financesolutiontypes?.data || []),
    [choices.financesolutiontypes?.data],
  )

  const showSector = formik.values.fs_type === 'business'
  const showGeographicalCoverage = formik.values.fs_type === 'ctf'
  const showUsedAnIncubator = BUSINESS_OR_FINANCIAL_MECHANISM_TYPES.includes(formik.values.fs_type)
  const showTafName = isTafNameVisible(formik.values.fs_type, formik.values.used_an_incubator)
  const showLocalEnterprise = LOCAL_ENTERPRISE_TYPES.includes(formik.values.fs_type)
  const showGenderSmart = BUSINESS_OR_FINANCIAL_MECHANISM_TYPES.includes(formik.values.fs_type)
  const showNumberOfSolutionsSupportedBy = NUMBER_OF_SOLUTIONS_SUPPORTED_BY_TYPES.includes(
    formik.values.fs_type,
  )
  const showSustainableFinanceMechanisms = formik.values.fs_type === 'financial_mechanism'

  const [SFMShowHelperText, setSFMShowHelperText] = useState()

  useEffect(() => {
    setSFMShowHelperText(displayHelp)
  }, [displayHelp])

  const handleSFMInfoIconClick = (event) => {
    setSFMShowHelperText(!SFMShowHelperText)

    event.stopPropagation()
  }

  const cancelButton = (
    <ButtonSecondary type="button" onClick={() => onDismiss(formik.resetForm)}>
      {t('buttons.cancel')}
    </ButtonSecondary>
  )

  const footer = (
    <StyledModalFooterWrapper>
      <StyledModalLeftFooter>
        {!!financeSolution && (
          <ButtonCaution onClick={handleDelete} disabled={isDeleting}>
            {t('buttons.remove_row')}
          </ButtonCaution>
        )}
      </StyledModalLeftFooter>
      <RightFooter>
        {cancelButton}
        <SaveButton
          formId="finance-solution-form"
          unsavedTitle={
            financeSolution
              ? t('gfcr.forms.finance_solutions.save')
              : t('gfcr.forms.finance_solutions.add')
          }
          saveButtonState={saveButtonState}
          formHasErrors={!!Object.keys(formik.errors).length}
          formDirty={isFormDirty}
        />
      </RightFooter>
    </StyledModalFooterWrapper>
  )

  const financeSolutionForm = () => {
    return (
      <form id="finance-solution-form" onSubmit={formik.handleSubmit}>
        <StyledModalInputRow>
          <InputNoRowSelectWithLabelAndValidation
            label={t('gfcr.forms.finance_solutions.fs_type')}
            id="fs-type-select"
            {...formik.getFieldProps('fs_type')}
            options={fsTypeOptions}
            helperText={
              <GfcrHelperLinks translationKey="gfcr.forms.finance_solutions.fs_type_helper" />
            }
            showHelperText={displayHelp}
            required={true}
            validationMessages={formik.errors.fs_type?.filter((e) => e.id === 'PCFRevenues') ?? []}
            validationType={
              formik.errors.fs_type?.some((e) => e.id === 'PCFRevenues') ? 'error' : undefined
            }
          />
        </StyledModalInputRow>
        <StyledModalInputRow>
          <InputNoRowWithLabelAndValidation
            label={t('gfcr.forms.finance_solutions.business_finance_solution_name')}
            id="finance-solution-input"
            type="text"
            {...formik.getFieldProps('name')}
            helperText={
              <GfcrHelperLinks translationKey="gfcr.forms.finance_solutions.name_helper" />
            }
            showHelperText={displayHelp}
            required={true}
            validationMessages={formik.errors.name?.filter((e) => e.id === 'Duplicate') ?? []}
            validationType={
              formik.errors.name?.some((e) => e.id === 'Duplicate') ? 'error' : undefined
            }
          />
        </StyledModalInputRow>
        {showSector && (
          <StyledModalInputRow>
            <InputNoRowSelectWithLabelAndValidation
              label={t('gfcr.forms.finance_solutions.sector')}
              id="sector-select"
              {...formik.getFieldProps('sector')}
              options={getOptions(choices.sectors.data)}
              helperText={
                <GfcrHelperLinks translationKey="gfcr.forms.finance_solutions.sector_helper" />
              }
              showHelperText={displayHelp}
              required={true}
            />
          </StyledModalInputRow>
        )}
        {showGeographicalCoverage && (
          <StyledModalInputRow>
            <InputNoRowSelectWithLabelAndValidation
              label={t('gfcr.forms.finance_solutions.geographical_coverage')}
              id="geographical-coverage-select"
              {...formik.getFieldProps('geographical_coverage')}
              options={getOptions(choices.geographicalcoverage?.data || [])}
              helperText={
                <GfcrHelperLinks translationKey="gfcr.forms.finance_solutions.geographical_coverage_helper" />
              }
              showHelperText={displayHelp}
              required={true}
            />
          </StyledModalInputRow>
        )}
        {showUsedAnIncubator && (
          <StyledModalInputRow>
            <InputNoRowSelectWithLabelAndValidation
              label={t('gfcr.forms.finance_solutions.used_an_incubator')}
              id="used-an-incubator-select"
              {...formik.getFieldProps('used_an_incubator')}
              options={[
                { value: 'none', label: t('no') },
                ...getOptions(choices.incubatortypes.data),
              ]}
              helperText={
                <GfcrHelperLinks translationKey="gfcr.forms.finance_solutions.used_an_incubator_helper" />
              }
              showHelperText={displayHelp}
              required={true}
            />
          </StyledModalInputRow>
        )}
        {showTafName && (
          <StyledModalInputRow>
            <InputNoRowWithLabelAndValidation
              label={t('gfcr.forms.finance_solutions.taf_name')}
              id="taf-name-input"
              type="text"
              {...formik.getFieldProps('taf_name')}
              helperText={
                <GfcrHelperLinks translationKey="gfcr.forms.finance_solutions.taf_name_helper" />
              }
              showHelperText={displayHelp}
            />
          </StyledModalInputRow>
        )}
        {showLocalEnterprise && (
          <StyledModalInputRow>
            <InputNoRowSelectWithLabelAndValidation
              label={t('gfcr.forms.finance_solutions.local_enterprise')}
              id="local-enterprise-select"
              {...formik.getFieldProps('local_enterprise')}
              options={[
                { value: true, label: t('yes') },
                { value: false, label: t('no') },
              ]}
              helperText={
                <GfcrHelperLinks translationKey="gfcr.forms.finance_solutions.local_enterprise_helper" />
              }
              showHelperText={displayHelp}
              required={true}
            />
          </StyledModalInputRow>
        )}
        {showGenderSmart && (
          <StyledModalInputRow>
            <InputNoRowSelectWithLabelAndValidation
              label={t('gfcr.forms.finance_solutions.gender_program_criteria')}
              id="gender-smart-select"
              {...formik.getFieldProps('gender_smart')}
              options={[
                { value: true, label: t('yes') },
                { value: false, label: t('no') },
              ]}
              helperText={
                <GfcrHelperLinks translationKey="gfcr.forms.finance_solutions.gender_program_criteria_helper" />
              }
              showHelperText={displayHelp}
              required={true}
            />
          </StyledModalInputRow>
        )}
        {showNumberOfSolutionsSupportedBy && (
          <StyledModalInputRow>
            <GfcrIntegerInputField
              label={t('gfcr.forms.finance_solutions.number_of_solutions_supported_by')}
              id="number_of_solutions_supported_by"
              maxValue={GFCR_MAX_INTEGER}
              formik={formik}
              helperText={
                <GfcrHelperLinks translationKey="gfcr.forms.finance_solutions.number_of_solutions_supported_by_helper" />
              }
              displayHelp={displayHelp}
              required
              noRow
            />
          </StyledModalInputRow>
        )}
        {showSustainableFinanceMechanisms && (
          <StyledModalInputRow>
            {/* Laid out to match the LabelContainer structure the shared mermaidInputs
            wrappers use (label, required indicator, info icon as flex siblings) so this
            field lines up with the rest of the form. The wrappers themselves can't be
            used here: they render a native select, which can't show chips or per-option
            checkboxes. */}
            <LabelContainer>
              <label
                id="sustainable-finance-mechanisms-label"
                htmlFor="sustainable-finance-mechanisms-select"
              >
                {t('gfcr.forms.finance_solutions.sustainable_finance_mechanisms')}
              </label>
              <span>
                <RequiredIndicator />
              </span>
              <IconButton type="button" onClick={(event) => handleSFMInfoIconClick(event)}>
                <IconInfo aria-label="info" />
              </IconButton>
            </LabelContainer>
            <CustomMuiSelect
              id="sustainable-finance-mechanisms-select"
              labelId="sustainable-finance-mechanisms-label"
              aria-describedby="sfm-helper"
              SelectDisplayProps={{ 'aria-required': 'true' }}
              multiple
              {...formik.getFieldProps('sustainable_finance_mechanisms')}
              input={<OutlinedInput id="select-multiple-chip" label="Chip" />}
              renderValue={(selected) =>
                selected?.length
                  ? getChips(selected, choices.sustainablefinancemechanisms.data)
                  : `${t('choose')}...`
              }
              displayEmpty={true}
            >
              {choices.sustainablefinancemechanisms.data.map((option) => (
                <CustomMenuItem
                  key={option.id}
                  value={option.id}
                  sx={{ fontSize: theme.typography.defaultFontSize }}
                >
                  <Checkbox
                    checked={formik
                      .getFieldProps('sustainable_finance_mechanisms')
                      .value.includes(option.id)}
                  />
                  {option.name}
                </CustomMenuItem>
              ))}
            </CustomMuiSelect>
            {SFMShowHelperText ? (
              <HelperText id="sfm-helper">
                <GfcrHelperLinks translationKey="gfcr.forms.finance_solutions.sustainable_finance_mechanisms_helper" />
              </HelperText>
            ) : null}
          </StyledModalInputRow>
        )}
        <hr />
        <StyledModalInputRow>
          <label id="notes-label" htmlFor="notes-input">
            {t('notes')}
          </label>
          <Textarea
            aria-labelledby={'notes-label'}
            id="notes-input"
            rows="6"
            {...formik.getFieldProps('notes')}
          />
        </StyledModalInputRow>
      </form>
    )
  }

  return (
    <Modal
      isOpen={isOpen}
      onDismiss={() => onDismiss(formik.resetForm)}
      title={
        financeSolution
          ? t('gfcr.forms.finance_solutions.update')
          : t('gfcr.forms.finance_solutions.add')
      }
      mainContent={financeSolutionForm()}
      footerContent={footer}
      maxWidth="65rem"
    />
  )
}

FinanceSolutionModal.propTypes = {
  indicatorSet: PropTypes.object,
  setIndicatorSet: PropTypes.func.isRequired,
  choices: choicesPropType.isRequired,
  financeSolution: PropTypes.object,
  isOpen: PropTypes.bool.isRequired,
  onDismiss: PropTypes.func.isRequired,
  displayHelp: PropTypes.bool,
}

export default FinanceSolutionModal
